/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { FunctionComponent, useState } from "react";

import { BorderRadiuses } from "@/tokens/border";
import { Colors } from "@/tokens/color";
import { TransitionEasings, TransitionSpeeds } from "@/tokens/motion";
import { Spacing } from "@/tokens/spacing";

import { RichText } from "@/ui/molecules/rich_text";

import { convertToRem } from "@/util/ui_util";

interface PricingCardPopoverProps {
    popoverContent: string;
    title: string;
}

export const PricingCardPopover: FunctionComponent<PricingCardPopoverProps> = ({
    popoverContent,
    title,
}) => {
    /**
     * State
     */
    const [isHovered, setIsHovered] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const shouldRenderPopover = isHovered || isActive || isFocused;

    /**
     * Interactivity
     */
    const handleMouseEnter = () => {
        setIsHovered(true);
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
    };

    const handleMouseDown = () => {
        setIsActive(true);
    };

    const handleMouseUp = () => {
        setIsActive(false);
    };

    const handleFocus = () => {
        setIsFocused(true);
    };

    const handleBlur = () => {
        setIsFocused(false);
    };

    /**
     * Styles
     */
    const containerStyles = css(
        {
            borderBottom: `1px dashed ${Colors["lighten-50"]}`,
            display: "inline",
            transition: `color ${TransitionSpeeds.mediumFast}ms ${TransitionEasings.easeInOut}, border-color ${TransitionSpeeds.mediumFast}ms ${TransitionEasings.easeInOut}`,
        },
        {
            "&:hover, &:focus, &:active": {
                borderColor: Colors["lighten-80"],
            },
        },
    );

    const popoverStyles = css({
        backgroundColor: Colors["neutral-90"],
        border: `1px solid ${Colors["lighten-20"]}`,
        borderRadius: BorderRadiuses.borderMedium,
        bottom: convertToRem(-20),
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        left: 0,
        opacity: shouldRenderPopover ? 1 : 0,
        padding: "10px",
        pointerEvents: shouldRenderPopover ? "auto" : "none",
        position: "absolute",
        right: 0,
        rowGap: Spacing["spacing-1"],
        transform: shouldRenderPopover
            ? "translateY(calc(100% - 10px))"
            : "translateY(100%)",
        transition: `opacity ${TransitionSpeeds.mediumFast}ms ${TransitionEasings.easeInOut}, transform ${TransitionSpeeds.mediumFast}ms ${TransitionEasings.easeInOut}`,
        width: "100%",
        zIndex: 999,
    });

    const caretStyles = css({
        left: "50%",
        position: "absolute",
        top: "0",
        transform: "translate(-50%, -50%)",
    });

    /**
     * Rendering
     */
    const renderCaret = () => (
        <svg
            css={caretStyles}
            fill="none"
            height="14"
            viewBox="0 0 14 14"
            width="14"
            xmlns="http://www.w3.org/2000/svg"
        >
            <mask
                fill="black"
                height="12.7279"
                id="path-1-outside-1_106_10322"
                maskUnits="userSpaceOnUse"
                width="12.7279"
                x="0.636039"
                y="0.585786"
            >
                <rect
                    fill="white"
                    height="12.7279"
                    width="12.7279"
                    x="0.636039"
                    y="0.585786"
                />

                <path d="M7 2L12.6569 7.65685L7 13.3137L1.34315 7.65685L7 2Z" />
            </mask>

            <path
                d="M7 2L12.6569 7.65685L7 13.3137L1.34315 7.65685L7 2Z"
                fill={Colors["neutral-90"]}
            />

            <path
                d="M7 2L7.70711 1.29289L7 0.585786L6.29289 1.29289L7 2ZM6.29289 2.70711L11.9497 8.36396L13.364 6.94975L7.70711 1.29289L6.29289 2.70711ZM2.05025 8.36396L7.70711 2.70711L6.29289 1.29289L0.636039 6.94975L2.05025 8.36396Z"
                fill="#3A3A3A"
                mask="url(#path-1-outside-1_106_10322)"
            />
        </svg>
    );

    const renderPopover = () => {
        return (
            <div css={popoverStyles}>
                {renderCaret()}

                <RichText config="PricingPopover">{popoverContent}</RichText>
            </div>
        );
    };

    return (
        <div
            css={containerStyles}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onMouseDown={handleMouseDown}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
        >
            {title}

            {renderPopover()}
        </div>
    );
};
